import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./MBLayerSelectionTab.css";
import CriticalFacilityMetadataView from "./CriticalFacilityMetadataView";
import TypeFilteredDropdown from "./TypeFilteredDropdown";
import {
  CREATE_LAYER_TYPE_OPTIONS,
  applyCriticalFacilityToParcelDraft,
  createLayerAcceptForType,
  isParcelCriticalFacility,
  normalizeCreateLayerType,
  removeCriticalFacilityFromParcelDraft,
} from "../../../helpers/modelBuilder/mbChatPureUtils";

const LINK_EDITABLE_FIELDS = [
  "id",
  "start_node_id",
  "end_node_id",
  "type",
  "name",
  "length",
  "maxmph",
  "lanes",
  "capacity",
  "fft",
  "start_osmid",
  "end_osmid",
  "geometry",
  "status",
];

function normalizeText(v) {
  return String(v == null ? "" : v).toLowerCase();
}

function editableKeysForLayer(layer, row, idKey) {
  const rowKeys = Object.keys(row || {}).filter(
    (k) => k !== idKey && k !== "path" && typeof row?.[k] !== "object"
  );
  if (layer !== "links") return rowKeys;

  const union = [...LINK_EDITABLE_FIELDS];
  rowKeys.forEach((k) => {
    if (!union.includes(k)) union.push(k);
  });
  return union;
}

function singularLayerLabel(layer) {
  if (layer === "links") return "link";
  if (layer === "nodes") return "node";
  if (layer === "parcels") return "parcel";
  if (layer === "other") return "row";
  if (layer === "polygon") return "polygon";
  return "item";
}

function getLayerConfig(layer, links, nodes, parcels, otherRows, polygonRows) {
  if (layer === "links") {
    return {
      items: links,
      idKey: "link_id",
      labelFn: (row) => String(row?.link_name || row?.name || row?.link_id || "Link"),
      title: "Selected links",
      subtitle: "Search by id or street name",
    };
  }

  if (layer === "parcels") {
    return {
      items: parcels,
      idKey: "parcel_id",
      labelFn: (row) => {
        const addr = [row?.site_stree || row?.SITE_STREE, row?.site_city || row?.SITE_CITY]
          .filter(Boolean)
          .join(", ");
        return String(addr || row?.parcel_name || row?.parcel_id || "Parcel");
      },
      title: "Selected parcels",
      subtitle: "Search by id or address",
    };
  }

  if (layer === "other") {
    return {
      items: otherRows,
      idKey: "_row_id",
      labelFn: (row) => {
        const preview = Object.entries(row || {})
          .filter(([k, v]) => k !== "_row_id" && v != null && v !== "" && typeof v !== "object")
          .slice(0, 1)
          .map(([k, v]) => `${k}: ${String(v)}`)
          .join("");
        return String(preview || `Row ${row?._row_id || ""}`);
      },
      title: "Selected rows",
      subtitle: "Search by row id or value",
    };
  }

  if (layer === "polygon") {
    return {
      items: polygonRows,
      idKey: "polygon_id",
      labelFn: (row) => String(row?.feature_name || row?.polygon_id || "Polygon"),
      title: "Polygon features",
      subtitle: "Search by polygon id or feature name",
    };
  }

  return {
    items: nodes,
    idKey: "node_id",
    labelFn: (row) => String(row?.name || row?.node_name || row?.node_id || "Node"),
    title: "Selected nodes",
    subtitle: "Search by id or name",
  };
}

function VirtualizedLayerList({
  items = [],
  renderRow,
  getRowKey,
  emptyNode = null,
  listIdentityKey = "",
  rowHeight = 44,
  overscan = 8,
  virtualizationThreshold = 500,
  className = "mb-layer-list",
  viewportHeight = 260,
}) {
  const listRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const safeItems = Array.isArray(items) ? items : [];
  const shouldVirtualize = safeItems.length >= virtualizationThreshold;

  useEffect(() => {
    setScrollTop(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [listIdentityKey]);

  if (!safeItems.length) {
    return <div className={className}>{emptyNode}</div>;
  }

  if (!shouldVirtualize) {
    return (
      <div className={className}>
        {safeItems.map((row, idx) => (
          <React.Fragment key={getRowKey(row, idx)}>{renderRow(row, idx)}</React.Fragment>
        ))}
      </div>
    );
  }

  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const visibleCount = Math.ceil(viewportHeight / rowHeight) + overscan * 2;
  const end = Math.min(safeItems.length, start + visibleCount);
  const topSpacer = start * rowHeight;
  const bottomSpacer = Math.max(0, (safeItems.length - end) * rowHeight);

  return (
    <div
      ref={listRef}
      className={className}
      style={{ maxHeight: viewportHeight, overflowY: "auto", display: "block" }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      {topSpacer > 0 ? <div style={{ height: topSpacer }} /> : null}
      {safeItems.slice(start, end).map((row, localIdx) => {
        const idx = start + localIdx;
        return (
          <div key={getRowKey(row, idx)} style={{ paddingBottom: 6 }}>
            {renderRow(row, idx)}
          </div>
        );
      })}
      {bottomSpacer > 0 ? <div style={{ height: bottomSpacer }} /> : null}
    </div>
  );
}

export default function MBLayerSelectionTab({
  activeLayer,
  activeLayerId = "",
  activeLayerName = "",
  activeLayerFileName = "",
  gisLayers = [],
  onChangeLayerSelection,
  onDuplicateLayer,
  onDeleteLayer,
  canDeleteLayer = false,
  onStartFilterLayer,
  onCancelFilterLayer,
  onSaveFilterLayer,
  onSaveLayerMeta,
  filterPolygonOptions = [],
  filterSelectedPolygonLayerIds = [],
  onToggleFilterPolygonLayer,
  isFilteringLayer = false,
  links = [],
  nodes = [],
  parcels = [],
  otherRows = [],
  polygonRows = [],
  selectedLinkIds,
  selectedNodeIds,
  selectedParcelIds,
  selectedOtherIds,
  selectedPolygonIds = new Set(),
  selectedPolygonColor = "#facc15",
  onChangeSelectedPolygonColor,
  onToggleLink,
  onToggleNode,
  onToggleParcel,
  onToggleOther,
  onTogglePolygon,
  onClearSelections,
  onSaveLayerEdits,
  onDeleteLayerRow,
  onDeleteSelectedRows,
  onCreateLayerFromUpload,
  onCreateLayerFromSavedPolygon,
  hasSavedMapPolygon = false,
  onDownloadLayerData,
  onDownloadSelectedPolygons,
  onSelectOppositeLink,
  onCenterSelection,
  isSaving = false,
  saveError = "",
  saveSuccess = "",
  addLinkDraftMode = "idle",
  addLinkDraftPath = [],
  addLinkDraftInfo = null,
  draftTopologyStep = "start",
  draftTopologyChoices = { start: "nearest_node", end: "leave_as_is" },
  draftNearestLinkOptions = { start: [], end: [] },
  draftNearestLinkSelected = { start: [], end: [] },
  addLinkNodeLayerOptions = [],
  addLinkSelectedNodeLayerId = "",
  addLinkNodeLayerLoading = false,
  onStartAddLinkDraft,
  onUndoAddLinkDraftPoint,
  onCancelAddLinkDraft,
  onFinishAddLinkDraft,
  onBackAddLinkNodeLayer,
  onAcceptAddLinkNodeLayer,
  onChangeAddLinkNodeLayer,
  onSetDraftTopologyChoice,
  onToggleDraftNearestLinkSelection,
  onAcceptDraftTopologyStep,
  onBackDraftTopologyStep,
  onSaveAddLinkDraft,
  onDiscardAddLinkDraft,
  criticalFacilitiesPanel = null,
}) {
  const [search, setSearch] = useState("");
  const [editDrafts, setEditDrafts] = useState({});
  const [editingById, setEditingById] = useState({});
  const [isEditingLayerMeta, setIsEditingLayerMeta] = useState(false);
  const [isCreatingLayer, setIsCreatingLayer] = useState(false);
  const [layerNameDraft, setLayerNameDraft] = useState(activeLayerName || "");
  const [newLayerName, setNewLayerName] = useState("");
  const [newLayerType, setNewLayerType] = useState("other");
  const [newLayerFile, setNewLayerFile] = useState(null);
  const layerFileInputRef = useRef(null);

  useEffect(() => {
    setSearch("");
  }, [activeLayer, activeLayerId]);

  useEffect(() => {
    setIsEditingLayerMeta(false);
    setLayerNameDraft(activeLayerName || "");
  }, [activeLayerId, activeLayerName]);

  const cfg = useMemo(
    () => getLayerConfig(activeLayer, links, nodes, parcels, otherRows, polygonRows),
    [activeLayer, links, nodes, parcels, otherRows, polygonRows]
  );
  const layerTypeLabels = {
    links: "Links",
    nodes: "Nodes",
    parcels: "Parcels",
    polygon: "Polygons",
    other: "Other",
  };
  const layerTypes = [...new Set((gisLayers || []).map((layer) => String(layer.type || "other")))];
  const createFileAccept = createLayerAcceptForType(newLayerType);

  const selectedSet =
    activeLayer === "links"
      ? selectedLinkIds
      : activeLayer === "parcels"
      ? selectedParcelIds
      : activeLayer === "other"
      ? selectedOtherIds
      : activeLayer === "polygon"
      ? selectedPolygonIds
      : selectedNodeIds;

  const toggle =
    activeLayer === "links"
      ? onToggleLink
      : activeLayer === "parcels"
      ? onToggleParcel
      : activeLayer === "other"
      ? onToggleOther
      : activeLayer === "polygon"
      ? onTogglePolygon
      : onToggleNode;

  const q = normalizeText(search.trim());
  const isLinkDraftEditMode = activeLayer === "links" && addLinkDraftMode !== "idle";
  const isCreateLayerMode = !isLinkDraftEditMode && isCreatingLayer;
  const isFilterableLayer =
    activeLayer === "links" || activeLayer === "nodes" || activeLayer === "parcels";
  const filterSelectedPolygonIdSet = useMemo(
    () => new Set((filterSelectedPolygonLayerIds || []).map((id) => String(id))),
    [filterSelectedPolygonLayerIds]
  );
  const scopedPrefix = `${String(activeLayerId || "")}:${String(activeLayer || "")}`;
  const toScopedRowId = useCallback((id) => `${scopedPrefix}:${String(id ?? "")}`, [scopedPrefix]);

  const selectedItems = useMemo(
    () => (cfg.items || []).filter((row) => selectedSet?.has?.(String(row?.[cfg.idKey]))),
    [cfg, selectedSet]
  );
  const canBulkDeleteSelected =
    (activeLayer === "links" || activeLayer === "nodes" || activeLayer === "parcels") &&
    selectedItems.length > 0;

  const filteredUnselected = useMemo(() => {
    const items = (cfg.items || []).filter((row) => !selectedSet?.has?.(String(row?.[cfg.idKey])));
    if (!q) return items;
    return items.filter((row) => {
      const idStr = normalizeText(row?.[cfg.idKey]);
      const labelStr = normalizeText(cfg.labelFn(row));
      const joined = normalizeText(
        Object.entries(row || {})
          .filter(([k, v]) => k !== cfg.idKey && v != null && typeof v !== "object")
          .map(([, v]) => String(v))
          .join(" ")
      );
      return idStr.includes(q) || labelStr.includes(q) || joined.includes(q);
    });
  }, [cfg, q, selectedSet]);

  const selectedListIdentityKey = useMemo(
    () => `${String(activeLayer || "")}:${String(activeLayerId || "")}:selected:${selectedItems.length}`,
    [activeLayer, activeLayerId, selectedItems.length]
  );
  const unselectedListIdentityKey = useMemo(
    () => `${String(activeLayer || "")}:${String(activeLayerId || "")}:all:${q}:${filteredUnselected.length}`,
    [activeLayer, activeLayerId, q, filteredUnselected.length]
  );
  const getSelectedRowKey = useCallback(
    (row, idx) => `sel-${toScopedRowId(row?.[cfg.idKey] ?? idx)}-${idx}`,
    [cfg.idKey, toScopedRowId]
  );
  const getUnselectedRowKey = useCallback(
    (row, idx) => `${toScopedRowId(row?.[cfg.idKey] ?? idx)}-${idx}`,
    [cfg.idKey, toScopedRowId]
  );
  const renderSelectedRow = useCallback(
    (row) => {
      const id = String(row?.[cfg.idKey]);
      return (
        <button className="mb-layer-row isSelected" onClick={() => toggle?.(id)}>
          <input type="checkbox" checked readOnly />
          <span className="mb-layer-id">{id}</span>
          <span className="mb-layer-label">{cfg.labelFn(row)}</span>
        </button>
      );
    },
    [cfg, toggle]
  );
  const renderUnselectedRow = useCallback(
    (row) => {
      const id = String(row?.[cfg.idKey]);
      return (
        <button
          className="mb-layer-row"
          onClick={() => {
            toggle?.(id);
            if (activeLayer === "polygon") onCenterSelection?.("polygon", id);
          }}
        >
          <input type="checkbox" checked={false} readOnly />
          <span className="mb-layer-id">{id}</span>
          <span className="mb-layer-label">{cfg.labelFn(row)}</span>
        </button>
      );
    },
    [activeLayer, cfg, onCenterSelection, toggle]
  );

  useEffect(() => {
    setEditDrafts((prev) => {
      const next = {};
      selectedItems.forEach((row) => {
        const id = String(row?.[cfg.idKey]);
        const scopedId = toScopedRowId(id);
        const prevDraft = prev[scopedId] || {};
        const merged = {};
        editableKeysForLayer(activeLayer, row, cfg.idKey).forEach((k) => {
          merged[k] = prevDraft[k] != null ? prevDraft[k] : String(row[k] ?? "");
        });
        next[scopedId] = merged;
      });
      return next;
    });
  }, [selectedItems, cfg.idKey, activeLayer, toScopedRowId]);

  useEffect(() => {
    setEditingById((prev) => {
      const next = {};
      selectedItems.forEach((row) => {
        const id = String(row?.[cfg.idKey]);
        const scopedId = toScopedRowId(id);
        if (prev[scopedId]) next[scopedId] = true;
      });
      return next;
    });
  }, [selectedItems, cfg.idKey, toScopedRowId]);

  const editableKeysForRow = (row, id) => {
    const base = editableKeysForLayer(activeLayer, row, cfg.idKey);
    const scopedId = toScopedRowId(id);
    const draft = editDrafts?.[scopedId] || {};
    const out = [...base];
    Object.keys(draft).forEach((k) => {
      if (k === cfg.idKey || k === "path") return;
      if (typeof draft?.[k] === "object") return;
      if (!out.includes(k)) out.push(k);
    });
    return out;
  };

  const setParcelCriticalFacility = (id, row, shouldBeCritical) => {
    if (activeLayer !== "parcels") return;
    const scopedId = toScopedRowId(id);
    setEditDrafts((prev) => {
      const current = prev?.[scopedId] || {};
      const next = shouldBeCritical
        ? applyCriticalFacilityToParcelDraft(row, current)
        : removeCriticalFacilityFromParcelDraft(row, current);
      return {
        ...prev,
        [scopedId]: {
          ...current,
          ...next,
        },
      };
    });
    setEditingById((prev) => ({ ...prev, [scopedId]: true }));
  };

  const saveOne = async (id) => {
    const scopedId = toScopedRowId(id);
    const payload = { [id]: editDrafts[scopedId] || {} };
    const ok = await onSaveLayerEdits?.(activeLayer, payload);
    if (ok) {
      setEditingById((prev) => ({ ...prev, [scopedId]: false }));
    }
  };

  const saveLayerMeta = async () => {
    const name = String(layerNameDraft || "").trim();
    if (!name) return;
    const ok = await onSaveLayerMeta?.(activeLayerId, { name });
    if (ok) setIsEditingLayerMeta(false);
  };

  const createLayer = async () => {
    const ok = await onCreateLayerFromUpload?.({
      name: newLayerName,
      file: newLayerFile,
      type: newLayerType,
    });
    if (!ok) return;
    setNewLayerName("");
    setNewLayerType("other");
    setNewLayerFile(null);
    setIsCreatingLayer(false);
    if (layerFileInputRef.current) {
      layerFileInputRef.current.value = "";
    }
  };

  const createLayerFromSavedPolygon = async () => {
    const ok = await onCreateLayerFromSavedPolygon?.({
      name: newLayerName,
    });
    if (!ok) return;
    setNewLayerName("");
    setNewLayerType("other");
    setNewLayerFile(null);
    setIsCreatingLayer(false);
    if (layerFileInputRef.current) {
      layerFileInputRef.current.value = "";
    }
  };

  const cancelCreateLayer = () => {
    setIsCreatingLayer(false);
    setNewLayerName("");
    setNewLayerType("other");
    setNewLayerFile(null);
    if (layerFileInputRef.current) {
      layerFileInputRef.current.value = "";
    }
  };

  return (
    <div className="mb-layer-shell">
      <div className="mb-layer-head">
        <div className="mb-layer-headActions">
          {!isCreateLayerMode && (
            <button
              className="mb-btn mb-btnSecondary"
              type="button"
              onClick={() => setIsCreatingLayer(true)}
              disabled={isSaving || isLinkDraftEditMode || isFilteringLayer}
            >
              Create layer
            </button>
          )}
          {!isCreateLayerMode && (
            <button
              className="mb-btn mb-btnSecondary"
              type="button"
              onClick={onClearSelections}
              disabled={!selectedItems.length || isFilteringLayer}
            >
              Clear selected
            </button>
          )}
        </div>
      </div>

      {isLinkDraftEditMode && (
        <>
          {saveError ? <div className="mb-saveMsg isError">{saveError}</div> : null}
          {saveSuccess ? <div className="mb-saveMsg isOk">{saveSuccess}</div> : null}
        </>
      )}

      {activeLayer === "links" && addLinkDraftMode === "drawing" && (
        <div className="mb-saveMsg">
          <div className="mb-layer-sectionTitle">Add Link: Drawing mode</div>
          <div style={{ fontSize: 13, color: "#d1d5db", marginBottom: 8 }}>
            Click points on the map to draw the link polyline.
          </div>
          <div className="mb-meta-actions" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="mb-btn mb-btnSecondary mb-meta-btn"
              onClick={onUndoAddLinkDraftPoint}
              disabled={addLinkDraftPath.length === 0}
            >
              Undo point
            </button>
            <button
              type="button"
              className="mb-btn mb-meta-btn"
              onClick={onFinishAddLinkDraft}
              disabled={addLinkDraftPath.length < 2}
            >
              Accept
            </button>
            <button
              type="button"
              className="mb-btn mb-btnSecondary mb-meta-btn"
              onClick={onCancelAddLinkDraft}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mb-btn mb-btnSecondary mb-meta-btn"
              onClick={onDiscardAddLinkDraft}
            >
              Discard draft
            </button>
          </div>
        </div>
      )}

      {activeLayer === "links" && addLinkDraftMode === "node_layer" && (
        <div className="mb-saveMsg">
          <div className="mb-layer-sectionTitle">Add Link: Select node layer</div>
          <div style={{ fontSize: 13, color: "#d1d5db", marginBottom: 8 }}>
            Pick the node layer used for topology. It will be loaded and shown with links.
          </div>
          <div style={{ marginBottom: 10 }}>
            <select
              className="mb-layer-select"
              value={String(addLinkSelectedNodeLayerId || "")}
              onChange={(e) => onChangeAddLinkNodeLayer?.(e.target.value)}
              disabled={isSaving || addLinkNodeLayerLoading}
            >
              {!addLinkNodeLayerOptions.length ? (
                <option value="">No node layers</option>
              ) : (
                addLinkNodeLayerOptions.map((opt) => (
                  <option key={String(opt.id)} value={String(opt.id)}>
                    {String(opt.name || opt.id)} ({String(opt.filename || "")})
                  </option>
                ))
              )}
            </select>
          </div>
          <div className="mb-meta-actions" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="mb-btn mb-btnSecondary mb-meta-btn"
              onClick={onBackAddLinkNodeLayer}
              disabled={isSaving || addLinkNodeLayerLoading}
            >
              Back
            </button>
            <button
              type="button"
              className="mb-btn mb-meta-btn"
              onClick={onAcceptAddLinkNodeLayer}
              disabled={
                isSaving ||
                addLinkNodeLayerLoading ||
                !String(addLinkSelectedNodeLayerId || "").trim() ||
                !addLinkNodeLayerOptions.length
              }
            >
              {addLinkNodeLayerLoading ? "Loading..." : "Accept"}
            </button>
            <button
              type="button"
              className="mb-btn mb-btnSecondary mb-meta-btn"
              onClick={onCancelAddLinkDraft}
              disabled={isSaving || addLinkNodeLayerLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {activeLayer === "links" && addLinkDraftMode === "ready" && addLinkDraftInfo && (
        <div className="mb-meta-card">
          <div className="mb-meta-title">
            Draft link {addLinkDraftInfo.linkId}
          </div>
          <div className="mb-meta-grid">
            <div className="mb-meta-row">
              <span className="mb-meta-key">Start endpoint node</span>
              <span className="mb-meta-val">
                {addLinkDraftInfo.endpointNodeIds?.start}
                {addLinkDraftInfo.activeDraftEndpoints?.start ? " (draft)" : " (existing)"}
              </span>
            </div>
            <div className="mb-meta-row">
              <span className="mb-meta-key">End endpoint node</span>
              <span className="mb-meta-val">
                {addLinkDraftInfo.endpointNodeIds?.end}
                {addLinkDraftInfo.activeDraftEndpoints?.end ? " (draft)" : " (existing)"}
              </span>
            </div>
          </div>
          <div className="mb-layer-sectionTitle">Toplogy</div>
          {draftTopologyStep === "start" && (
            <>
              <div style={{ fontSize: 13, color: "#d1d5db", marginBottom: 8 }}>
                Start node:
              </div>
              <div className="mb-meta-actions" style={{ marginTop: 0, justifyContent: "flex-start" }}>
                <button
                  type="button"
                  className={`mb-btn mb-meta-btn ${draftTopologyChoices?.start === "nearest_node" ? "" : "mb-btnSecondary"}`}
                  onClick={() => onSetDraftTopologyChoice?.("start", "nearest_node")}
                >
                  Connect to nearest node
                </button>
                <button
                  type="button"
                  className={`mb-btn mb-meta-btn ${draftTopologyChoices?.start === "nearest_link" ? "" : "mb-btnSecondary"}`}
                  onClick={() => onSetDraftTopologyChoice?.("start", "nearest_link")}
                >
                  Connect to nearest link
                </button>
                <button
                  type="button"
                  className={`mb-btn mb-meta-btn ${draftTopologyChoices?.start === "leave_as_is" ? "" : "mb-btnSecondary"}`}
                  onClick={() => onSetDraftTopologyChoice?.("start", "leave_as_is")}
                >
                  Default
                </button>
              </div>
              {draftTopologyChoices?.start === "nearest_link" && (
                <div className="mb-meta-grid" style={{ marginTop: 8 }}>
                  <div className="mb-meta-row">
                    <span className="mb-meta-key">Select link(s)</span>
                    <span className="mb-meta-val">
                      {(draftNearestLinkOptions?.start || []).length
                        ? (draftNearestLinkOptions.start || []).map((id) => (
                            <label key={`start-opt-${id}`} style={{ display: "inline-flex", alignItems: "center", marginRight: 12 }}>
                              <input
                                type="checkbox"
                                checked={(draftNearestLinkSelected?.start || []).includes(String(id))}
                                onChange={() => onToggleDraftNearestLinkSelection?.("start", String(id))}
                              />
                              <span style={{ marginLeft: 6 }}>{id}</span>
                            </label>
                          ))
                        : "No nearby links found."}
                    </span>
                  </div>
                </div>
              )}
              <div className="mb-meta-actions" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="mb-btn mb-btnSecondary mb-meta-btn"
                  onClick={onBackDraftTopologyStep}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="mb-btn mb-meta-btn mb-map-polygon-create-btn"
                  onClick={onAcceptDraftTopologyStep}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="mb-btn mb-btnSecondary mb-meta-btn"
                  onClick={onDiscardAddLinkDraft}
                  disabled={isSaving}
                >
                  Discard draft
                </button>
              </div>
            </>
          )}
          {draftTopologyStep === "end" && (
            <>
              <div style={{ fontSize: 13, color: "#d1d5db", marginBottom: 8 }}>
                End node:
              </div>
              <div className="mb-meta-actions" style={{ marginTop: 0, justifyContent: "flex-start" }}>
                <button
                  type="button"
                  className={`mb-btn mb-meta-btn ${draftTopologyChoices?.end === "nearest_node" ? "" : "mb-btnSecondary"}`}
                  onClick={() => onSetDraftTopologyChoice?.("end", "nearest_node")}
                >
                  Connect to nearest node
                </button>
                <button
                  type="button"
                  className={`mb-btn mb-meta-btn ${draftTopologyChoices?.end === "nearest_link" ? "" : "mb-btnSecondary"}`}
                  onClick={() => onSetDraftTopologyChoice?.("end", "nearest_link")}
                >
                  Connect to nearest link
                </button>
                <button
                  type="button"
                  className={`mb-btn mb-meta-btn ${draftTopologyChoices?.end === "leave_as_is" ? "" : "mb-btnSecondary"}`}
                  onClick={() => onSetDraftTopologyChoice?.("end", "leave_as_is")}
                >
                  Default
                </button>
              </div>
              {draftTopologyChoices?.end === "nearest_link" && (
                <div className="mb-meta-grid" style={{ marginTop: 8 }}>
                  <div className="mb-meta-row">
                    <span className="mb-meta-key">Select link(s)</span>
                    <span className="mb-meta-val">
                      {(draftNearestLinkOptions?.end || []).length
                        ? (draftNearestLinkOptions.end || []).map((id) => (
                            <label key={`end-opt-${id}`} style={{ display: "inline-flex", alignItems: "center", marginRight: 12 }}>
                              <input
                                type="checkbox"
                                checked={(draftNearestLinkSelected?.end || []).includes(String(id))}
                                onChange={() => onToggleDraftNearestLinkSelection?.("end", String(id))}
                              />
                              <span style={{ marginLeft: 6 }}>{id}</span>
                            </label>
                          ))
                        : "No nearby links found."}
                    </span>
                  </div>
                </div>
              )}
              <div className="mb-meta-actions" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="mb-btn mb-btnSecondary mb-meta-btn"
                  onClick={onBackDraftTopologyStep}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="mb-btn mb-meta-btn"
                  onClick={onAcceptDraftTopologyStep}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="mb-btn mb-btnSecondary mb-meta-btn"
                  onClick={onDiscardAddLinkDraft}
                  disabled={isSaving}
                >
                  Discard draft
                </button>
              </div>
            </>
          )}
          {addLinkDraftMode === "ready" && draftTopologyStep === "done" && (
            <div style={{ fontSize: 13, color: "#bbf7d0", marginBottom: 8 }}>
              Topology accepted. You can now save this link.
            </div>
          )}
          {addLinkDraftMode === "ready" && draftTopologyStep === "done" && (
            <>
              <div className="mb-layer-sectionTitle">Save</div>
              <div className="mb-meta-actions" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="mb-btn mb-btnSecondary mb-meta-btn"
                  onClick={onBackDraftTopologyStep}
                  disabled={isSaving}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="mb-btn mb-meta-btn"
                  onClick={onSaveAddLinkDraft}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save draft to cloud"}
                </button>
                <button
                  type="button"
                  className="mb-btn mb-btnSecondary mb-meta-btn"
                  onClick={onDiscardAddLinkDraft}
                  disabled={isSaving}
                >
                  Discard draft
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {!isLinkDraftEditMode && !isCreateLayerMode && !isFilteringLayer && (
      <div className="mb-layer-controls">
        <TypeFilteredDropdown
          groupLabel="Layers"
          selectedId={activeLayerId}
          selectedLabel={activeLayerName ? `${activeLayerName} (${activeLayer})` : "Select a layer"}
          emptyLabel="No layers match the selected types."
          types={layerTypes.map((type) => ({ value: type, label: layerTypeLabels[type] || type }))}
          items={(gisLayers || []).map((layer) => ({
            id: layer.id,
            type: String(layer.type || "other"),
            label: `${layer.name} (${layer.type})`,
          }))}
          onSelect={onChangeLayerSelection}
          disabled={isSaving}
        />
        <div className="mb-layer-selectedId">Layer ID: {activeLayerId || "-"}</div>
      </div>
      )}

      {!isLinkDraftEditMode && !isCreateLayerMode && !isFilteringLayer && (
        <div className="mb-layer-metaSection">
          <div className="mb-layer-sectionTitle">Layer details</div>
          <div className="mb-layer-metaGrid">
            <div className="mb-layer-metaRow">
              <span className="mb-layer-metaKey">Name</span>
              {isEditingLayerMeta ? (
                <input
                  className="mb-meta-input"
                  value={layerNameDraft}
                  onChange={(e) => setLayerNameDraft(e.target.value)}
                />
              ) : (
                <span className="mb-layer-metaVal">{activeLayerName || "-"}</span>
              )}
            </div>
            <div className="mb-layer-metaRow">
              <span className="mb-layer-metaKey">Type</span>
              <span className="mb-layer-metaVal">{activeLayer || "-"}</span>
            </div>
            <div className="mb-layer-metaRow">
              <span className="mb-layer-metaKey">Loaded {activeLayer === "other" ? "rows" : activeLayer === "polygon" ? "polygons" : activeLayer || "items"}</span>
              <span className="mb-layer-metaVal">{cfg.items.length.toLocaleString()}</span>
            </div>
            <div className="mb-layer-metaRow">
              <span className="mb-layer-metaKey">File</span>
              <span className="mb-layer-metaVal">{activeLayerFileName || "-"}</span>
            </div>
          </div>
          <div className="mb-meta-actions" style={{ marginTop: 8 }}>
            {!isEditingLayerMeta ? (
              <>
                <button
                  type="button"
                  className="mb-btn mb-btnSecondary mb-meta-btn"
                  disabled={isSaving || !activeLayerId}
                  onClick={onDuplicateLayer}
                >
                  Duplicate layer
                </button>
                {activeLayer === "links" ? (
                  <button
                    type="button"
                    className="mb-btn mb-meta-btn"
                    disabled={isSaving || !activeLayerId || addLinkDraftMode === "drawing"}
                    onClick={onStartAddLinkDraft}
                  >
                    Add link
                  </button>
                ) : null}
                {canDeleteLayer ? (
                  <button
                    type="button"
                    className="mb-btn mb-btnDanger mb-meta-btn"
                    disabled={isSaving || !activeLayerId}
                    onClick={onDeleteLayer}
                  >
                    Delete layer
                  </button>
                ) : null}
                <button
                  type="button"
                  className="mb-btn mb-btnSecondary mb-meta-btn"
                  disabled={isSaving || !activeLayerId || !activeLayerFileName}
                  onClick={onDownloadLayerData}
                >
                  Download data
                </button>
                {isFilterableLayer && (
                  <button
                    type="button"
                    className="mb-btn mb-btnSecondary mb-meta-btn"
                    disabled={isSaving || !activeLayerId}
                    onClick={onStartFilterLayer}
                  >
                    Filter layer
                  </button>
                )}
                <button
                  type="button"
                  className="mb-btn mb-meta-btn"
                  disabled={isSaving || !activeLayerId}
                  onClick={() => setIsEditingLayerMeta(true)}
                >
                  Edit layer
                </button>
              </>
            ) : (
              <button
                type="button"
                className="mb-btn mb-meta-btn"
                disabled={isSaving || !String(layerNameDraft || "").trim()}
                onClick={saveLayerMeta}
              >
                {isSaving ? "Saving..." : "Save layer"}
              </button>
            )}
            {isEditingLayerMeta ? (
              <button
                type="button"
                className="mb-btn mb-btnSecondary mb-meta-btn"
                disabled={isSaving}
                onClick={() => {
                  setIsEditingLayerMeta(false);
                  setLayerNameDraft(activeLayerName || "");
                }}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      )}

      {!isLinkDraftEditMode &&
        !isCreateLayerMode &&
        !isFilteringLayer &&
        activeLayer === "parcels" &&
        criticalFacilitiesPanel}

      {isCreateLayerMode && (
        <div className="mb-layer-metaSection">
          <div className="mb-layer-sectionTitle">Create layer</div>
          {saveError ? <div className="mb-saveMsg isError">{saveError}</div> : null}
          {saveSuccess ? <div className="mb-saveMsg isOk">{saveSuccess}</div> : null}
          <div className="mb-layer-metaGrid">
            <div className="mb-layer-metaRow">
              <span className="mb-layer-metaKey">Layer name</span>
              <input
                className="mb-meta-input"
                value={newLayerName}
                onChange={(e) => setNewLayerName(e.target.value)}
                placeholder="Enter layer name"
              />
            </div>
            <div className="mb-layer-metaRow">
              <span className="mb-layer-metaKey">Layer type</span>
              <select
                className="mb-meta-input"
                value={newLayerType}
                onChange={(e) => {
                  const nextType = normalizeCreateLayerType(e.target.value || "other");
                  setNewLayerType(nextType);
                  setNewLayerFile(null);
                  if (layerFileInputRef.current) layerFileInputRef.current.value = "";
                }}
              >
                {CREATE_LAYER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-layer-metaRow">
              <span className="mb-layer-metaKey">Upload data</span>
              <input
                ref={layerFileInputRef}
                type="file"
                accept={createFileAccept}
                className="mb-meta-input"
                onChange={(e) => setNewLayerFile(e.target.files?.[0] || null)}
              />
            </div>
            {newLayerType === "polygon" && (
              <div className="mb-layer-metaRow">
                <span className="mb-layer-metaKey">Map polygon</span>
                <div className="mb-map-polygon-create-cell">
                  <button
                    type="button"
                    className="mb-btn mb-meta-btn mb-map-polygon-create-btn"
                    disabled={isSaving || !String(newLayerName || "").trim() || !hasSavedMapPolygon}
                    onClick={createLayerFromSavedPolygon}
                    title={
                      hasSavedMapPolygon
                        ? "Create a polygon layer from the saved polygon on the map"
                        : "Save a polygon on the map first"
                    }
                  >
                    {isSaving ? "Saving..." : "Create from saved map polygon"}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="mb-meta-actions" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="mb-btn mb-btnSecondary mb-meta-btn"
              disabled={isSaving}
              onClick={cancelCreateLayer}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mb-btn mb-meta-btn"
              disabled={isSaving || !String(newLayerName || "").trim() || !newLayerFile}
              onClick={createLayer}
            >
              {isSaving ? "Saving..." : "Save layer"}
            </button>
          </div>
        </div>
      )}

      {!isLinkDraftEditMode && !isCreateLayerMode && isFilteringLayer && (
        <div className="mb-layer-metaSection">
          <div className="mb-layer-sectionTitle">Filter layer by polygons</div>
          {saveError ? <div className="mb-saveMsg isError">{saveError}</div> : null}
          {saveSuccess ? <div className="mb-saveMsg isOk">{saveSuccess}</div> : null}
          <div style={{ fontSize: 13, color: "#d1d5db", marginBottom: 8 }}>
            Select one or more polygon layers (zones) to include.
          </div>
          <div className="mb-layer-listWrap">
            <div className="mb-layer-list">
              {(filterPolygonOptions || []).map((row) => {
                const id = String(row?.id || "");
                const checked = filterSelectedPolygonIdSet.has(id);
                return (
                  <label
                    key={`filter-zone-${id}`}
                    className="mb-layer-row"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isSaving}
                      onChange={() => onToggleFilterPolygonLayer?.(id)}
                    />
                    <span className="mb-layer-id">{id}</span>
                    <span className="mb-layer-label">
                      {String(row?.name || row?.id || "")} ({String(row?.filename || "")})
                    </span>
                  </label>
                );
              })}
              {!filterPolygonOptions.length && (
                <div className="mb-layer-empty">No polygon layers available.</div>
              )}
            </div>
          </div>
          <div className="mb-meta-actions" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="mb-btn mb-btnSecondary mb-meta-btn"
              disabled={isSaving}
              onClick={onCancelFilterLayer}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mb-btn mb-meta-btn"
              disabled={isSaving || !filterSelectedPolygonLayerIds.length}
              onClick={onSaveFilterLayer}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {!isLinkDraftEditMode && !isCreateLayerMode && !isFilteringLayer && (
        <div className="mb-layer-searchWrap">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-layer-search"
            placeholder={cfg.subtitle}
          />
        </div>
      )}

      {!isLinkDraftEditMode && !isCreateLayerMode && !isFilteringLayer && (
      <div className="mb-layer-listWrap">
        {!!selectedItems.length && (
          <>
            <div className="mb-layer-sectionTitle">{cfg.title} ({selectedItems.length})</div>
            {canBulkDeleteSelected && (
              <div className="mb-layer-selectedActions">
                <button
                  type="button"
                  className="mb-btn mb-btnDanger mb-meta-btn"
                  disabled={isSaving}
                  onClick={() =>
                    onDeleteSelectedRows?.(
                      activeLayer,
                      selectedItems.map((row) => String(row?.[cfg.idKey] ?? "")).filter(Boolean)
                    )
                  }
                >
                  Delete all selected
                </button>
              </div>
            )}
            {activeLayer === "polygon" && (
              <div className="mb-layer-selectedActions">
                <label className="mb-layer-colorControl">
                  <span>Selected color</span>
                  <input
                    type="color"
                    value={selectedPolygonColor}
                    disabled={isSaving}
                    onChange={(e) => onChangeSelectedPolygonColor?.(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="mb-btn mb-btnSecondary mb-meta-btn"
                  disabled={isSaving}
                  onClick={onDownloadSelectedPolygons}
                >
                  Download selected polygons
                </button>
              </div>
            )}
            <VirtualizedLayerList
              items={selectedItems}
              listIdentityKey={selectedListIdentityKey}
              getRowKey={getSelectedRowKey}
              renderRow={renderSelectedRow}
            />
          </>
        )}

        <div className="mb-layer-sectionTitle">
          All {activeLayerName || activeLayer}
        </div>
        <VirtualizedLayerList
          items={filteredUnselected}
          listIdentityKey={unselectedListIdentityKey}
          getRowKey={getUnselectedRowKey}
          renderRow={renderUnselectedRow}
          emptyNode={<div className="mb-layer-empty">No results.</div>}
        />
      </div>
      )}

      {!isLinkDraftEditMode && !isCreateLayerMode && !isFilteringLayer && !!selectedItems.length && (
        <div className="mb-meta-wrap">
          <div className="mb-layer-sectionTitle">Metadata and edit</div>
          {saveError ? <div className="mb-saveMsg isError">{saveError}</div> : null}
          {saveSuccess ? <div className="mb-saveMsg isOk">{saveSuccess}</div> : null}
          {selectedItems.map((row) => {
            const id = String(row?.[cfg.idKey]);
            const scopedId = toScopedRowId(id);
            const editableKeys = editableKeysForRow(row, id);
            const isEditing = !!editingById[scopedId];
            const isCriticalFacility =
              activeLayer === "parcels" ? isParcelCriticalFacility(row, editDrafts?.[scopedId] || {}) : false;
            return (
              <div key={`meta-${scopedId}`} className="mb-meta-card">
                <div className="mb-meta-title">{singularLayerLabel(activeLayer)} {id}</div>
                <div className="mb-meta-grid">
                  <div className="mb-meta-row">
                    <span className="mb-meta-key">ID</span>
                    <span className="mb-meta-val">{id}</span>
                  </div>
                  {editableKeys.map((k) => {
                    const shown = isEditing ? (editDrafts?.[scopedId]?.[k] ?? "") : String(row?.[k] ?? "");
                    const isCriticalFacilityMetadata =
                      activeLayer === "parcels" && String(k).toLowerCase() === "critical_facility_metadata";
                    return (
                      <div
                        key={`${scopedId}-${k}`}
                        className={`mb-meta-row ${isCriticalFacilityMetadata ? "mb-meta-rowStructured" : ""}`}
                      >
                        <span className="mb-meta-key">
                          {isCriticalFacilityMetadata ? "Critical facility metadata" : k}
                        </span>
                        {isEditing ? (
                          isCriticalFacilityMetadata ? (
                            <textarea
                              className="mb-meta-input mb-criticalMetadata-editor"
                              value={shown}
                              onChange={(e) =>
                                setEditDrafts((prev) => ({
                                  ...prev,
                                  [scopedId]: {
                                    ...(prev[scopedId] || {}),
                                    [k]: e.target.value,
                                  },
                                }))
                              }
                            />
                          ) : (
                            <input
                              className="mb-meta-input"
                              value={shown}
                              onChange={(e) =>
                                setEditDrafts((prev) => ({
                                  ...prev,
                                  [scopedId]: {
                                    ...(prev[scopedId] || {}),
                                    [k]: e.target.value,
                                  },
                                }))
                              }
                            />
                          )
                        ) : isCriticalFacilityMetadata ? (
                          <CriticalFacilityMetadataView value={row?.[k]} />
                        ) : (
                          <span className="mb-meta-val">{shown}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mb-meta-actions">
                  {!isEditing ? (
                    <>
                      <button
                        type="button"
                        className="mb-btn mb-btnSecondary mb-meta-btn"
                        onClick={() => onCenterSelection?.(activeLayer, id)}
                      >
                        Center Map
                      </button>
                      <button
                        type="button"
                        className="mb-btn mb-meta-btn"
                        disabled={isSaving}
                        onClick={() => setEditingById((prev) => ({ ...prev, [scopedId]: true }))}
                      >
                        Edit
                      </button>
                      {activeLayer === "links" && (
                        <button
                          type="button"
                          className="mb-btn mb-btnSecondary mb-meta-btn"
                          disabled={isSaving}
                          onClick={() => onSelectOppositeLink?.(id)}
                        >
                          Select opposite link
                        </button>
                      )}
                      {(activeLayer === "links" ||
                        activeLayer === "nodes" ||
                        activeLayer === "parcels" ||
                        activeLayer === "other") && (
                        <button
                          type="button"
                          className="mb-btn mb-btnDanger mb-meta-btn"
                          disabled={isSaving}
                          onClick={() => onDeleteLayerRow?.(activeLayer, id)}
                        >
                          Delete
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      {activeLayer === "parcels" && (
                        <label
                          className="mb-btn mb-btnSecondary mb-meta-btn"
                          style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                        >
                          <input
                            type="checkbox"
                            checked={isCriticalFacility}
                            disabled={isSaving}
                            onChange={(e) => setParcelCriticalFacility(id, row, !!e.target.checked)}
                          />
                          <span>Critical facility</span>
                        </label>
                      )}
                      <button
                        type="button"
                        className="mb-btn mb-btnSecondary mb-meta-btn"
                        disabled={isSaving}
                        onClick={() => onCenterSelection?.(activeLayer, id)}
                      >
                        Center Map
                      </button>
                      <button
                        type="button"
                        className="mb-btn mb-meta-btn"
                        disabled={isSaving}
                        onClick={() => saveOne(id)}
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        className="mb-btn mb-btnSecondary mb-meta-btn"
                        disabled={isSaving}
                        onClick={() =>
                          setEditingById((prev) => ({ ...prev, [scopedId]: false }))
                        }
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
